import React, { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { ShoppingBag, User, Menu, X } from "lucide-react";
import { useLang } from "@/context/LangContext";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import CartSheet from "@/components/CartSheet";
import { useSiteMedia } from "@/context/SiteMediaContext";

const Header = () => {
  const { t, lang, setLang } = useLang();
  const { count, setOpen } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const { media } = useSiteMedia();

  const links = [
    { to: "/", label: t("nav.home") },
    { to: "/shop", label: t("nav.shop") },
    { to: "/gift-card", label: t("nav.giftCard") },
    { to: "/about", label: t("nav.about") },
    { to: "/reviews", label: t("nav.reviews") },
    { to: "/skin-test", label: t("nav.skinTest") },
    { to: "/reseller", label: t("nav.reseller") },
    { to: "/contact", label: t("nav.contact") },
  ];

  const linkClass = ({ isActive }) =>
    `text-xs uppercase tracking-[0.18em] transition-colors hover:text-charcoal ${
      isActive ? "text-charcoal" : "text-stone-500"
    }`;

  return (
    <header
      data-testid="site-header"
      className="sticky top-0 z-50 bg-white border-b border-stone-200"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-20 gap-4">
          {/* Left: logo + mobile menu button */}
          <div className="flex items-center gap-3">
            <button
              className="xl:hidden text-charcoal"
              onClick={() => setMobileOpen((v) => !v)}
              data-testid="mobile-menu-toggle"
              aria-label="Menu"
            >
              {mobileOpen ? <X size={22} strokeWidth={1.5} /> : <Menu size={22} strokeWidth={1.5} />}
            </button>
            <Link
              to="/"
              data-testid="logo-link"
              className="block"
            >
              <img
                src={media("logo")}
                alt="Kaós Italian Luxury Cosmetics®"
                className="h-11 md:h-12 w-auto object-contain"
              />
            </Link>
          </div>

          {/* Center: nav (desktop) */}
          <nav className="hidden xl:flex items-center gap-6">
            {links.map((l) => (
              <NavLink key={l.to} to={l.to} className={linkClass} data-testid={`nav-${l.to === "/" ? "home" : l.to.slice(1)}`}>
                <span className="whitespace-nowrap">{l.label}</span>
              </NavLink>
            ))}
          </nav>

          {/* Right: actions */}
          <div className="flex items-center gap-4 md:gap-6 justify-end">
            <div className="hidden sm:flex items-center gap-1 text-xs tracking-widest">
              <button
                onClick={() => setLang("it")}
                data-testid="lang-it"
                className={`transition-colors ${lang === "it" ? "text-charcoal" : "text-stone-400 hover:text-stone-600"}`}
              >
                IT
              </button>
              <span className="text-stone-300">/</span>
              <button
                onClick={() => setLang("en")}
                data-testid="lang-en"
                className={`transition-colors ${lang === "en" ? "text-charcoal" : "text-stone-400 hover:text-stone-600"}`}
              >
                EN
              </button>
            </div>
            <button
              onClick={() => navigate(user ? (user.role === "admin" ? "/admin" : "/account") : "/login")}
              data-testid="account-button"
              aria-label="Account"
              className="text-charcoal hover:text-champagne transition-colors"
            >
              <User size={20} strokeWidth={1.5} />
            </button>
            <button
              onClick={() => setOpen(true)}
              data-testid="cart-button"
              aria-label="Cart"
              className="relative text-charcoal hover:text-champagne transition-colors"
            >
              <ShoppingBag size={20} strokeWidth={1.5} />
              {count > 0 && (
                <span
                  data-testid="cart-count"
                  className="absolute -top-2 -right-2 bg-charcoal text-white text-[10px] w-4 h-4 rounded-full flex items-center justify-center"
                >
                  {count}
                </span>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile nav */}
      {mobileOpen && (
        <div className="xl:hidden border-t border-stone-200 bg-white px-6 py-6 flex flex-col gap-5">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              onClick={() => setMobileOpen(false)}
              className="text-sm uppercase tracking-[0.18em] text-stone-600"
              data-testid={`mobile-nav-${l.to === "/" ? "home" : l.to.slice(1)}`}
            >
              {l.label}
            </NavLink>
          ))}
          <div className="flex items-center gap-2 text-xs tracking-widest pt-2 border-t border-stone-100">
            <button onClick={() => setLang("it")} className={lang === "it" ? "text-charcoal" : "text-stone-400"}>IT</button>
            <span className="text-stone-300">/</span>
            <button onClick={() => setLang("en")} className={lang === "en" ? "text-charcoal" : "text-stone-400"}>EN</button>
          </div>
        </div>
      )}

      <CartSheet />
    </header>
  );
};

export default Header;
